import React from 'react';
import { Box, Button, IconButton, Slider, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import StraightenIcon from '@mui/icons-material/Straighten';
import FullscreenIcon from '@mui/icons-material/Fullscreen';

const CrossSectionView = ({
    t,
    crossSectionEnabled,
    crossSectionFullscreen,
    setCrossSectionFullscreen,
    crossSectionAxis,
    setCrossSectionAxis,
    crossSectionPosition,
    setCrossSectionPosition,
    crossSectionAngle,
    setCrossSectionAngle,
    crossSectionPlaneOpacity,
    setCrossSectionPlaneOpacity,
    maxillaFile,
    mandibleFile,
    csMeasureMode,
    setCsMeasureMode,
    csMeasurements,
    setCsMeasurements,
    csMeasurePending,
    setCsMeasurePending,
    csMeasureOverlays,
    crossSectionCanvasRef,
    csOutlineCanvasRef,
    csOutlineCameraRef,
}) => {
    if (!crossSectionEnabled || (!maxillaFile && !mandibleFile)) return null;

    const tFn = t || ((key, fallback) => fallback || key);

    const handleMeasureClick = (e) => {
        if (!csMeasureMode) return;
        const canvas = csOutlineCanvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const nx = (e.clientX - rect.left) / rect.width;
        const ny = 1 - (e.clientY - rect.top) / rect.height;
        const cam = csOutlineCameraRef.current;
        const wx = cam.left + nx * (cam.right - cam.left);
        const wy = cam.bottom + ny * (cam.top - cam.bottom);
        const pt = { x: wx, y: wy };
        if (csMeasurePending) {
            setCsMeasurements(prev => [...prev, [csMeasurePending, pt]]);
            setCsMeasurePending(null);
        } else {
            setCsMeasurePending(pt);
        }
    };

    const renderMeasureOverlay = (variant) => {
        if (csMeasureOverlays.lines.length === 0 && !csMeasureOverlays.pending) return null;
        const lineColor = variant === 'fullscreen' ? '#d32f2f' : '#ffff44';
        const dotSize = variant === 'fullscreen' ? 8 : 6;
        const pendingDotSize = variant === 'fullscreen' ? 10 : 8;
        const fontSize = variant === 'fullscreen' ? 11 : 9;
        return (
            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
                    {csMeasureOverlays.lines.map((ln, i) => (
                        <line key={i} x1={`${ln.x1Pct}%`} y1={`${ln.y1Pct}%`} x2={`${ln.x2Pct}%`} y2={`${ln.y2Pct}%`} stroke={lineColor} strokeWidth="1.5" strokeDasharray="4,3" />
                    ))}
                </svg>
                {csMeasureOverlays.lines.map((ln, i) => (
                    <React.Fragment key={i}>
                        <Box sx={{ position: 'absolute', left: `${ln.x1Pct}%`, top: `${ln.y1Pct}%`, width: dotSize, height: dotSize, borderRadius: '50%', backgroundColor: lineColor, border: '1px solid #fff', transform: 'translate(-50%, -50%)' }} />
                        <Box sx={{ position: 'absolute', left: `${ln.x2Pct}%`, top: `${ln.y2Pct}%`, width: dotSize, height: dotSize, borderRadius: '50%', backgroundColor: lineColor, border: '1px solid #fff', transform: 'translate(-50%, -50%)' }} />
                        <Box sx={{
                            position: 'absolute',
                            left: `${(ln.x1Pct + ln.x2Pct) / 2}%`,
                            top: `${(ln.y1Pct + ln.y2Pct) / 2}%`,
                            transform: 'translate(-50%, -130%)',
                            backgroundColor: variant === 'fullscreen' ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.75)',
                            color: variant === 'fullscreen' ? '#fff' : lineColor,
                            fontSize,
                            fontWeight: 700,
                            px: variant === 'fullscreen' ? '6px' : '4px',
                            py: variant === 'fullscreen' ? '2px' : '1px',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                        }}>
                            {ln.dist.toFixed(2)} mm
                        </Box>
                    </React.Fragment>
                ))}
                {csMeasureOverlays.pending && (
                    <Box sx={{
                        position: 'absolute',
                        left: `${csMeasureOverlays.pending.xPct}%`,
                        top: `${csMeasureOverlays.pending.yPct}%`,
                        width: pendingDotSize,
                        height: pendingDotSize,
                        borderRadius: '50%',
                        backgroundColor: lineColor,
                        border: '2px solid #fff',
                        transform: 'translate(-50%, -50%)',
                        animation: 'pulse 1s infinite',
                    }} />
                )}
            </Box>
        );
    };

    const renderMeasureButtons = (variant) => {
        const iconSize = variant === 'fullscreen' ? 14 : 12;
        const rulerSize = variant === 'fullscreen' ? 16 : 14;
        return (
            <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: '2px' }}>
                {csMeasureMode && csMeasurements.length > 0 && (
                    <Tooltip title={tFn('viewer3d.clearAll', 'Clear All')} placement="left">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCsMeasurements([]); setCsMeasurePending(null); }}
                            sx={{ p: '3px', backgroundColor: 'rgba(255,68,68,0.25)', color: '#ff6644', '&:hover': { backgroundColor: 'rgba(255,68,68,0.4)' } }}>
                            <DeleteIcon sx={{ fontSize: iconSize }} />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title={csMeasureMode ? 'Exit Measure' : 'Measure'} placement="left">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCsMeasureMode(prev => !prev); setCsMeasurePending(null); }}
                        sx={{ p: '3px', backgroundColor: csMeasureMode ? 'rgba(255,255,68,0.25)' : 'rgba(0,0,0,0.5)', color: csMeasureMode ? '#ffff44' : 'rgba(255,255,255,0.5)', '&:hover': { backgroundColor: csMeasureMode ? 'rgba(255,255,68,0.4)' : 'rgba(0,0,0,0.7)' } }}>
                        <StraightenIcon sx={{ fontSize: rulerSize }} />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    return (
        <Box
            sx={crossSectionFullscreen ? {
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 50,
                backgroundColor: '#1a1a1a',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            } : {
                position: 'absolute',
                bottom: 6,
                left: 6,
                width: 'min(390px, calc(100% - 12px))',
                height: 'min(390px, calc(50% - 6px))',
                borderRadius: '6px',
                overflow: 'hidden',
                border: csMeasureMode ? '1px solid rgba(255,255,68,0.5)' : '1px solid rgba(126,203,245,0.3)',
                backgroundColor: '#1a1a1a',
                zIndex: 10,
                cursor: csMeasureMode ? 'crosshair' : 'pointer',
                '&:hover': { borderColor: csMeasureMode ? 'rgba(255,255,68,0.7)' : 'rgba(0,0,0,0.3)' },
            }}
            onClick={crossSectionFullscreen ? undefined : (e) => {
                if (csMeasureMode) {
                    handleMeasureClick(e);
                } else {
                    setCrossSectionFullscreen(true);
                }
            }}
        >
            {crossSectionFullscreen ? (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 0.5 }}>
                            <Typography sx={{ color: '#7ecbf5', fontSize: 12, fontWeight: 600 }}>{tFn('viewer3d.crossSection', 'Cross Section')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', gap: '4px' }}>
                                    {['y', 'x', 'z'].map(axis => (
                                        <Button
                                            key={axis}
                                            size="small"
                                            onClick={() => setCrossSectionAxis(axis)}
                                            sx={{
                                                minWidth: 0, px: 1, py: 0.25,
                                                fontSize: 9, fontWeight: 600, textTransform: 'none',
                                                color: crossSectionAxis === axis ? '#7ecbf5' : 'rgba(255,255,255,0.35)',
                                                border: `1px solid ${crossSectionAxis === axis ? 'rgba(126,203,245,0.3)' : 'rgba(255,255,255,0.08)'}`,
                                                borderRadius: '4px',
                                            }}
                                        >
                                            {axis === 'y' ? tFn('viewer3d.horiz', 'Horiz') : axis === 'x' ? tFn('viewer3d.sagittal', 'Sagittal') : tFn('viewer3d.coronal', 'Coronal')}
                                        </Button>
                                    ))}
                                </Box>
                                <IconButton
                                    onClick={() => setCrossSectionFullscreen(false)}
                                    sx={{ color: '#fff', p: '4px', backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
                                >
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pb: 0.5, minWidth: 0 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, fontWeight: 600, flexShrink: 0 }}>{tFn('viewer3d.position', 'Position')}</Typography>
                            <Slider
                                value={crossSectionPosition}
                                onChange={(_, v) => setCrossSectionPosition(v)}
                                min={-1} max={1} step={0.01}
                                size="small"
                                sx={{ flex: 2, minWidth: 40, color: '#7ecbf5', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 } }}
                            />
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, fontWeight: 600, flexShrink: 0 }}>{tFn('viewer3d.rotation', 'Rotation')}</Typography>
                            <Slider
                                value={crossSectionAngle}
                                onChange={(_, v) => setCrossSectionAngle(v)}
                                min={-90} max={90} step={1}
                                size="small"
                                sx={{ flex: 1.5, minWidth: 30, color: '#f5c77e', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 } }}
                            />
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 9, fontWeight: 600, flexShrink: 0 }}>Opacity</Typography>
                            <Slider
                                value={crossSectionPlaneOpacity}
                                onChange={(_, v) => setCrossSectionPlaneOpacity(v)}
                                min={0} max={1} step={0.01}
                                size="small"
                                sx={{ flex: 1, minWidth: 30, color: '#a5f57e', height: 3, '& .MuiSlider-thumb': { width: 10, height: 10 } }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                        <Box sx={{ flex: 1, position: 'relative', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
                            <canvas ref={crossSectionCanvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
                        </Box>
                        <Box
                            onClick={handleMeasureClick}
                            sx={{ flex: 1, position: 'relative', cursor: csMeasureMode ? 'crosshair' : 'default' }}
                        >
                            <canvas ref={csOutlineCanvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
                            {renderMeasureOverlay('fullscreen')}
                            {renderMeasureButtons('fullscreen')}
                            {csMeasureMode && (
                                <Typography sx={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center', color: 'rgba(25,118,210,0.7)', fontSize: 9, fontWeight: 600, pointerEvents: 'none' }}>
                                    {csMeasurePending ? 'Click second point' : 'Click first point'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </>
            ) : (
                <>
                    <canvas ref={csOutlineCanvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
                    {renderMeasureOverlay('inset')}
                    <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: '2px' }}>
                        {csMeasureMode && csMeasurements.length > 0 && (
                            <Tooltip title={tFn('viewer3d.clearAll', 'Clear All')} placement="left">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCsMeasurements([]); setCsMeasurePending(null); }}
                                    sx={{ p: '3px', backgroundColor: 'rgba(255,68,68,0.25)', color: '#ff6644', '&:hover': { backgroundColor: 'rgba(255,68,68,0.4)' } }}>
                                    <DeleteIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={csMeasureMode ? 'Exit Measure' : 'Measure'} placement="left">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCsMeasureMode(prev => !prev); setCsMeasurePending(null); }}
                                sx={{ p: '3px', backgroundColor: csMeasureMode ? 'rgba(255,255,68,0.25)' : 'rgba(0,0,0,0.5)', color: csMeasureMode ? '#ffff44' : 'rgba(255,255,255,0.5)', '&:hover': { backgroundColor: csMeasureMode ? 'rgba(255,255,68,0.4)' : 'rgba(0,0,0,0.7)' } }}>
                                <StraightenIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={tFn('viewer3d.fullscreen', 'Fullscreen')} placement="left">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCrossSectionFullscreen(true); }}
                                sx={{ p: '3px', backgroundColor: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}>
                                <FullscreenIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Typography sx={{
                        position: 'absolute', bottom: 2, left: 0, right: 0, textAlign: 'center',
                        color: csMeasureMode ? 'rgba(255,255,68,0.6)' : 'rgba(126,203,245,0.6)',
                        fontSize: 7, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', pointerEvents: 'none',
                    }}>
                        {csMeasureMode ? (csMeasurePending ? 'Click second point' : 'Click first point') : 'Cross Section'}
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default CrossSectionView;
